import React, { useEffect, useMemo } from "react";
import { InfoOutlined, Lock } from "@mui/icons-material";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import analytics from "fond/analytics";
import { selectVersionsByProjectId, useGetVersionScenarioConfigurationQuery } from "fond/api";
import { COLLABORATION_LAYER_FILTER, COLLABORATION_SOURCE_LAYER, CTS_SOURCE } from "fond/draw/modes/cts/constants";
import { useCanICollaboration } from "fond/hooks/useCanICollaboration";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import { Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";

import ScenarioPanel from "../CostToServe/ScenarioPanel";
import AnalysisPanel from "../NBN/CostToServe/AnalysisPanel";
import ExcludeDrawPanel from "../NBN/CostToServe/ExcludeDrawPanel";

import GeneratePanel from "./GeneratePanel";
import OptimizePanel from "./OptimizePanel";
import SetupPanel from "./SetupPanel";

interface CollaborationCTSPanelProps {
  /**
   * The project currently being viewed / edited
   */
  project: Project;
}

const CollaborationCTSPanel: React.FC<CollaborationCTSPanelProps> = ({ project }) => {
  const { canAnalyseCTS, canSetupCTS, isLoading: isLoadingCollaboration, isDisabled } = useCanICollaboration();
  const { hasConfig: hasScenarioConfiguration, isLoading: isLoadingScenarioConfiguration } = useGetScenarioConfiguration({
    projectId: project.ID,
  });
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: scenarioConfiguration } = useGetVersionScenarioConfigurationQuery(versionId ?? skipToken);
  const { workflowState } = useCustomWorkflow({
    versionId: versionId ?? undefined,
    key: WORKFLOW_KEYS.COLLABORATION_CTS.ROOT,
  });

  const versions = useAppSelector((state) => selectVersionsByProjectId(state, project.ID));
  const hasVersions = (versions?.length ?? 0) > 1;
  const originalVersion = versions?.at(-1);
  const isGenerating = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  // Track panel opened (only once when data is loaded)
  useEffect(() => {
    if (!isLoadingCollaboration && !isLoadingScenarioConfiguration) {
      analytics.track("collaboration.cts.panel.opened", {
        versionId,
        hasExistingConfiguration: !!scenarioConfiguration,
        canSetupCTS,
        canAnalyseCTS,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCollaboration, isLoadingScenarioConfiguration]);

  const accordionPanes: AccordionStepperPane[] = useMemo(() => {
    const panes: AccordionStepperPane[] = [];

    const setupPanel = {
      header: "Setup cost to serve",
      id: "boundary",
      content: <SetupPanel configuration={scenarioConfiguration} isDisabled={isDisabled || hasVersions} />,
      isComplete: !!scenarioConfiguration,
      isLoading: false,
    };

    const generatePanel = {
      header: "Generate",
      id: "generate",
      content: <GeneratePanel versionId={versionId} isDisabled={isDisabled || hasVersions} />,
      isComplete: workflowState === "COMPLETE",
      isLoading: isGenerating,
    };

    const analysisPanel = {
      header: "Analysis",
      id: "analysis",
      content: <AnalysisPanel workflowKey={WORKFLOW_KEYS.COLLABORATION_CTS.ITERATION} isDisabled={isDisabled} showOptimize />,
      isComplete: hasVersions,
      isLoading: false,
    };

    if (canSetupCTS) {
      panes.push(setupPanel, generatePanel, analysisPanel);
    } else if (canAnalyseCTS) {
      panes.push(analysisPanel);
    }

    return panes;
  }, [canAnalyseCTS, canSetupCTS, hasVersions, isDisabled, isGenerating, scenarioConfiguration, versionId, workflowState]);

  if (!project) return null;

  if (isLoadingCollaboration || isLoadingScenarioConfiguration)
    return (
      <Box p={1}>
        <SkeletonLoadingGrid spacing={1} items={[{ height: 160 }, { height: 40 }, { height: 40 }]} />
      </Box>
    );

  const getContent = () => {
    if (hasScenarioConfiguration && versionId !== originalVersion?.ID) {
      return <ScenarioPanel project={project} />;
    }
    if (accordionPanes.length === 0) {
      return <NonIdealState size="small" icon={<InfoOutlined />} description={<>There are no design tools available.</>} />;
    }

    return <StackedNavigation rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />} screens={navigationScreens} />;
  };

  const costingLayerKey = scenarioConfiguration?.ToolConfigurations?.CostToServe?.CostingLayer?.LayerKey || COLLABORATION_SOURCE_LAYER;

  const navigationScreens = [
    {
      id: "optimize",
      name: "Optimize",
      component: <OptimizePanel project={project} currency={scenarioConfiguration?.Currency} isReadOnly={isDisabled} />,
    },
    {
      id: "excludeDraw",
      name: "Exclude service locations",
      useDefaultHeader: false,
      component: (
        <ExcludeDrawPanel
          workflowKey={WORKFLOW_KEYS.COLLABORATION_CTS.ITERATION}
          source={CTS_SOURCE}
          sourceLayer={costingLayerKey}
          layerFilter={COLLABORATION_LAYER_FILTER}
          identifierLabel="FOND ID"
          identifierProperty="FondID"
          colorByCTS
          colorByOptions={[
            { label: "Shared cost", layerId: `output/collaboration_cost_to_serve/${costingLayerKey}/SharedCost`, propertyKey: "SharedCost" },
            { label: "Accumulated cost", layerId: `output/collaboration_cost_to_serve/${costingLayerKey}/AccCost`, propertyKey: "AccCost" },
            { label: "Individual cost", layerId: `output/collaboration_cost_to_serve/${costingLayerKey}/IndivCost`, propertyKey: "IndivCost" },
          ]}
          currency={scenarioConfiguration?.Currency}
        />
      ),
    },
  ];

  return (
    <Box data-testid="collaboration-cts-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <>{getContent()}</>
      )}
    </Box>
  );
};

export default CollaborationCTSPanel;
